import { Box } from '@mui/material';
import { DocumentUpload } from 'iconsax-react';
import { type FC, useContext, useEffect, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import Enums from '@core/types/api/Enums';
import type { File as SlabFile } from '@core/types/api/File/File';
import { type FileEntityType, UploadItemStatus, fileEntityPathSegment } from '@core/types/file';
import { hasValue } from '@core/utils/equality';
import { useSlabMutation } from '@web/hooks/useSlabMutation';
import { ServerErrorMessage, useSlabQuery } from '@web/hooks/useSlabQuery';
import { useConfirmDialog } from '@web/providers/ConfirmProvider/useConfirmDialog';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { QueryError } from '@web/utils/Query';

import { EmptyState } from '../EmptyState/EmptyState';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { FileList } from './FileList';
import { FileUploadArea } from './FileUploadArea';
import { useFileDownload } from './useFileDownload';
import type { FileUploadsManager } from './useFileUploads';
import { useFileUploads } from './useFileUploads';

type Props = {
  entityType: FileEntityType;
  entityId: string;
  uploadsManager?: FileUploadsManager;
};

export const EntityFilesPanel: FC<Props> = ({ entityType, entityId, uploadsManager }) => {
  const ctx = useContext(SlabContext);
  const flagEnabled = ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagFileAttachments]);
  const canMarkSensitive = ctx.userInfo.hasPermissions([Enums.Permission.CanViewSensitiveFiles]);

  const ownUploadsManager = useFileUploads({ entityType, entityId });
  const manager = uploadsManager ?? ownUploadsManager;

  const deleteConfirmationAlert = useConfirmDialog();
  const toast = useToast({ subjectName: 'file' });
  const { requestDownload, downloadingFileId } = useFileDownload({
    onError: (error) => {
      toast.error({ message: `Failed to download file: ${ServerErrorMessage(error)}` });
    },
  });
  const deleteMutation = useSlabMutation('DELETE file by ID');

  const filesResult = useSlabQuery(
    'GET entity files by entity ID',
    {
      pathParams: { entityPath: fileEntityPathSegment(entityType), id: entityId },
      queryParams: { page: 0, perPage: 1 },
    },
    { enabled: flagEnabled },
  );
  const { data: files, isLoading, isError } = filesResult;

  useEffect(() => {
    const registered = manager.items.filter(
      (item) => item.status === UploadItemStatus.Done && hasValue(item.file),
    );
    registered.forEach((item) => manager.remove(item.id));
  }, [manager]);

  const previousEntityIdRef = useRef(entityId);
  useEffect(() => {
    if (previousEntityIdRef.current !== entityId) {
      previousEntityIdRef.current = entityId;
      manager.reset();
    }
  }, [entityId, manager]);

  if (!flagEnabled) {
    return null;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <div>ERROR</div>;
  }

  // Rejected/Failed queue items don't count toward "not empty" -- they'll never become a
  // real file, and still render their own row (with retry/remove) in FileUploadArea above,
  // so a user whose only attempt failed should still see the empty-state invite to try again.
  const hasPendingUpload = manager.items.some(
    (item) =>
      item.status === UploadItemStatus.Uploading ||
      item.status === UploadItemStatus.Registering ||
      item.status === UploadItemStatus.Done,
  );
  const isEmpty = (files?.count ?? 0) === 0 && !hasPendingUpload;

  const handleDelete = (file: SlabFile): void => {
    deleteConfirmationAlert.alert({
      title: 'Confirm delete',
      description: `Are you sure you want to delete "${file.fileName}"?`,
      actions: [
        { label: 'Cancel', variant: 'slab-primary' },
        {
          label: 'Delete',
          variant: 'slab-secondary',
          onClick: (): void => {
            deleteMutation.mutate(
              { args: { pathParams: { id: file.id }, body: {} } },
              {
                onSuccess: (): void => {
                  toast.success({ message: 'File deleted.' });
                },
                onError: (error: QueryError): void => {
                  toast.error({ message: `Failed to delete file: ${ServerErrorMessage(error)}` });
                },
              },
            );
          },
        },
      ],
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <FileUploadArea manager={manager} canMarkSensitive={canMarkSensitive} />
      {isEmpty ? (
        <EmptyState
          icon={DocumentUpload}
          title='No files attached yet'
          description='Drag and drop or browse to add one.'
        />
      ) : (
        <FileList
          entityType={entityType}
          entityId={entityId}
          onDownload={requestDownload}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          downloadingFileId={downloadingFileId}
        />
      )}
    </Box>
  );
};
